import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { TextField, Label, Input, FieldError, TextFieldProps } from 'react-aria-components'
import { DSText, DSSpacer } from '@zoe/ds-web'
import styled from 'styled-components'

const StyledInput = styled(Input)`
  width: 100%;
  padding: var(--padding-input-y) var(--padding-input-x);
  background-color: var(--color-surface-input);
  border: 1px solid var(--color-border-input);
  border-radius: var(--radius-8);
  color: var(--color-text-input);
  font-family: var(--font-sans-serif-light);
  font-size: var(--font-size-input);
  line-height: var(--line-height-input);
  outline: none;
  transition: border 0.3s ease-in;

  &:focus {
    border: 1px solid var(--color-border-input-focus);
  }

  &:disabled {
    background-color: var(--color-surface-input-disabled);
  }
`

interface DSTextInputProps<T extends FieldValues> extends Omit<TextFieldProps, 'children'> {
  name: Path<T>
  control: Control<T>
  label?: string
  placeholder?: string
  type?: 'text' | 'email' | 'tel' | 'password'
  isRequired?: boolean
  isDisabled?: boolean
  testId?: string
}

export const DSTextInput = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  type = 'text',
  isRequired = false,
  isDisabled = false,
  testId,
}: DSTextInputProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          validationBehavior="aria"
          type={type}
          isRequired={isRequired}
          isInvalid={fieldState.invalid}
          {...field}
        >
          {label && (
            <>
              <Label>
                <DSText variant="fluid-paragraph-200" {...(testId ? { 'data-testid': `${testId}-label` } : {})}>
                  {label}
                </DSText>
              </Label>
              <DSSpacer size={8} direction="vertical" />
            </>
          )}
          <StyledInput
            disabled={isDisabled}
            placeholder={placeholder || ''}
            {...(testId ? { 'data-testid': `${testId}-input` } : {})}
          />
          {fieldState.error && (
            <FieldError>
              <DSText variant="fluid-label-100" color="error" {...(testId ? { 'data-testid': `${testId}-error` } : {})}>
                {fieldState.error.message}
              </DSText>
            </FieldError>
          )}
        </TextField>
      )}
    />
  )
}
